import React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Alert, Space, Skeleton, Card, Tag, Descriptions } from 'antd';
import {
    CodeOutlined,
    PlayCircleOutlined,
    CopyOutlined,
    ExpandAltOutlined,
    ShrinkOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { DataCrafterTemplateFormStore, DataCrafterScriptCompilationStore } from '../../stores';
import './DataCrafterTemplatePickerScript.less';
import SyntaxHighlighter from 'react-syntax-highlighter';
import ReactJson from 'react-json-view';

interface DataCrafterTemplatePickerScriptProps {
    store: DataCrafterTemplateFormStore;
    scriptCompilationStore: DataCrafterScriptCompilationStore;
}

const DataCrafterTemplatePickerScript = ({ store, scriptCompilationStore }: DataCrafterTemplatePickerScriptProps) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [isCopied, setIsCopied] = React.useState(false);

    const handleCompile = async () => {
        try {
            await store.compileTemplateVariantPickerScriptWithProgress();
        } catch (error) {
            console.error('Failed to compile picker script:', error);
        }
    };

    const handleTest = async () => {
        try {
            await store.testTemplateVariantPickerScript();
        } catch (error) {
            console.error('Failed to test picker script:', error);
        }
    };

    const handleCopy = () => {
        if (store.currentTemplateVariantPickerCompilationState.templateScript) {
            navigator.clipboard
                .writeText(store.currentTemplateVariantPickerCompilationState.templateScript)
                .then(() => {
                    setIsCopied(true);
                    setTimeout(() => setIsCopied(false), 2000);
                });
        }
    };

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const renderLoadingState = () => {
        if (!store.currentTemplateVariantPickerCompilationState.isLoadingTemplateScript) {
            return null;
        }

        return (
            <div className="picker-script-loading">
                <Skeleton active />
                <div className="compilation-message">
                    <p className="message">Compiling picker script...</p>
                </div>
            </div>
        );
    };

    const renderTestResults = () => {
        // Show testing loading state
        if (scriptCompilationStore.isTestingPickerScript) {
            return (
                <div className="picker-script-testing">
                    <Skeleton active />
                    <div className="testing-message">
                        <p className="message">Testing picker script...</p>
                    </div>
                </div>
            );
        }

        // Show test results if available
        if (scriptCompilationStore.pickerScriptTestResult) {
            const result = scriptCompilationStore.pickerScriptTestResult;
            const hasError = result.error !== null;

            return (
                <div className="picker-script-test-results">
                    <Alert
                        message={hasError ? 'Test completed with errors' : 'Test completed successfully'}
                        type={hasError ? 'warning' : 'success'}
                        showIcon
                        className="picker-test-alert"
                    />

                    <Card title="Picker Script Test Result" className="test-result-card" size="small">
                        <Descriptions column={1} size="small">
                            <Descriptions.Item label="Variant ID">
                                <code>{result.variantId}</code>
                            </Descriptions.Item>
                            <Descriptions.Item label="Variant Name">
                                <strong>{result.name}</strong>
                            </Descriptions.Item>
                            <Descriptions.Item label="Condition">
                                <code className="condition-text">{result.condition}</code>
                            </Descriptions.Item>
                            <Descriptions.Item label="Is Default">
                                <Tag
                                    color={result.isDefault ? 'green' : 'default'}
                                    icon={result.isDefault ? <CheckCircleOutlined /> : undefined}
                                >
                                    {result.isDefault ? 'Yes' : 'No'}
                                </Tag>
                            </Descriptions.Item>
                            {hasError && (
                                <Descriptions.Item label="Error">
                                    <Alert
                                        message={result.error}
                                        type="error"
                                        icon={<ExclamationCircleOutlined />}
                                        className="error-detail"
                                    />
                                </Descriptions.Item>
                            )}
                            {result.usedDataExcerpts && (
                                <Descriptions.Item label="Used Data Excerpts">
                                    <ReactJson src={result.usedDataExcerpts} displayDataTypes={false} collapsed={1} />
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </Card>
                </div>
            );
        }

        return null;
    };

    return (
        <div className="data-crafter-template-picker-script">
            <div className="picker-script-header">
                <div className="action-buttons">
                    <Space>
                        <Button
                            type="primary"
                            icon={<CodeOutlined />}
                            onClick={handleCompile}
                            loading={store.currentTemplateVariantPickerCompilationState.isLoadingTemplateScript}
                            disabled={store.isBusyWithScriptOperation}
                        >
                            Compile Picker Script
                        </Button>
                        <Button
                            icon={<PlayCircleOutlined />}
                            onClick={handleTest}
                            loading={scriptCompilationStore.isTestingPickerScript}
                            disabled={store.isBusyWithScriptOperation}
                        >
                            Test Picker
                        </Button>
                    </Space>
                </div>
                {store.currentTemplateVariantPickerCompilationState.templateScript && (
                    <div className="utility-buttons">
                        <Space>
                            <Button
                                size="small"
                                icon={isExpanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
                                onClick={toggleExpand}
                            >
                                {isExpanded ? 'Collapse' : 'Expand'}
                            </Button>
                            <Button
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={handleCopy}
                                type={isCopied ? 'text' : 'default'}
                            >
                                {isCopied ? 'Copied!' : 'Copy'}
                            </Button>
                        </Space>
                    </div>
                )}
            </div>

            {store.currentTemplateVariantPickerCompilationState.templateScriptError && (
                <Alert
                    message={store.currentTemplateVariantPickerCompilationState.templateScriptError}
                    type="error"
                    showIcon
                    className="picker-script-error"
                />
            )}

            {renderLoadingState()}
            {renderTestResults()}

            {!store.currentTemplateVariantPickerCompilationState.isLoadingTemplateScript &&
                store.currentTemplateVariantPickerCompilationState.templateScript && (
                    <div className={`picker-script-container ${isExpanded ? 'expanded' : ''}`}>
                        <SyntaxHighlighter language="python" className="picker-script-content">
                            {store.currentTemplateVariantPickerCompilationState.templateScript}
                        </SyntaxHighlighter>
                    </div>
                )}
        </div>
    );
};

export default observer(DataCrafterTemplatePickerScript);
